import { useState, type ReactElement } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'

import {
  CoaTemplateAccountForm,
  coaTemplateAccountsQuery,
  coaTemplateQuery,
  useUpdateCoaTemplateAccount,
} from '~/features/coa-templates'
import type { AppError } from '~/lib/result'

/**
 * `/admin/finance/reference-accounts/$templateId/accounts/$accountId/edit`
 * — edit one account row. Reuses the already-fetched accounts list query
 * (the detail page loads all of a template's accounts at once) rather than
 * adding a single-account GET endpoint.
 */
export const Route = createFileRoute(
  '/admin/finance/reference-accounts/$templateId/accounts/$accountId/edit',
)({
  component: EditAccountPage,
})

function EditAccountPage(): ReactElement {
  const { templateId, accountId } = Route.useParams()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)

  const accountsQuery = useQuery(coaTemplateAccountsQuery(templateId))
  const templateQuery = useQuery(coaTemplateQuery(templateId))
  const updateAccount = useUpdateCoaTemplateAccount()

  const account = accountsQuery.data?.accounts.find((a) => a.id === accountId) ?? null

  const backTo = () =>
    void navigate({
      to: '/admin/finance/reference-accounts/$templateId',
      params: { templateId },
    })

  if (accountsQuery.isPending) {
    return (
      <div className="ka-page" aria-busy="true">
        <div className="ka-sk ka-sk--line-flex-2" />
      </div>
    )
  }
  if (accountsQuery.error || !account) {
    return (
      <div className="ka-page">
        <div className="ka-card">
          <div className="ka-empty">
            <div className="ka-empty-title">Account not found</div>
            <Link
              to="/admin/finance/reference-accounts/$templateId"
              params={{ templateId }}
              className="ka-btn ka-btn--secondary ka-btn--sm"
            >
              <ArrowLeft size={14} aria-hidden="true" /> Back to template
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="ka-page">
      <header className="ka-page-header">
        <div>
          <button type="button" className="ka-btn ka-btn--ghost ka-btn--sm" onClick={backTo}>
            <ArrowLeft size={14} aria-hidden="true" /> Back
          </button>
          <h1 className="ka-page-header-title">
            Edit {account.code} · {account.name}
          </h1>
        </div>
      </header>

      <CoaTemplateAccountForm
        templateId={templateId}
        codeFormatHint={templateQuery.data?.code_format_hint ?? null}
        account={account}
        isSubmitting={updateAccount.isPending}
        serverError={serverError}
        onCancel={backTo}
        onSubmit={(body) => {
          setServerError(null)
          updateAccount.mutate(
            { templateId, accountId, body: body as Parameters<typeof updateAccount.mutate>[0]['body'] },
            {
              onSuccess: backTo,
              onError: (cause) => setServerError(errorToMessage(cause)),
            },
          )
        }}
      />
    </div>
  )
}

function errorToMessage(cause: unknown): string {
  const appError = cause as AppError
  return appError?.message ?? 'Something went wrong. Please try again.'
}
